import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { colors } from "../../colors/colors";
import { validateAdminCredentials } from "../../axios/admin";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";

import {
  loginContainerSx,
  paperSx,
  titleSx,
  formSx,
  labelSx,
  passwordBoxSx,
  buttonSubmitSx,
  textFieldSx,
} from "./styles";
import { HomeIcon } from "../../assets/icon";
import useUserStore from "../../stores/userStore";
import { AdminCreds } from "../../types/admin";

interface AdminLoginScreenProps {
  onAdminLogin: ({ adminLogin, adminPassword }: AdminCreds) => void;
}

const AdminLoginScreen = ({ onAdminLogin }: AdminLoginScreenProps) => {
  const navigate = useNavigate();
  const [loginInput, setLoginInput] = useState<string>("");
  const [passwordInput, setPasswordInput] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { logout } = useUserStore();

  const handleAdminLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!loginInput.trim() || !passwordInput.trim()) {
      logout();
      navigate("/login");
      return;
    }
    try {
      const response = await validateAdminCredentials({
        adminLogin: loginInput,
        adminPassword: passwordInput,
      });
      if (response.valid) {
        onAdminLogin({ adminLogin: loginInput, adminPassword: passwordInput });
      } else {
        logout();
        navigate("/login");
      }
    } catch {
      logout();
      navigate("/login");
    }
  };

  return (
    <Box sx={loginContainerSx}>
      <Paper elevation={6} sx={paperSx}>
        <Typography sx={titleSx}>Sign In as Admin</Typography>
        <Box component="form" onSubmit={handleAdminLogin} sx={formSx}>
          <Box>
            <Typography
              variant="body2"
              fontWeight="medium"
              component="label"
              htmlFor="login"
              sx={labelSx}
            >
              Login
            </Typography>
            <TextField
              sx={textFieldSx}
              id="login"
              variant="outlined"
              fullWidth
              required
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              placeholder="Enter your login"
            />
          </Box>
          <Box sx={passwordBoxSx}>
            <Typography
              variant="body2"
              fontWeight="medium"
              component="label"
              htmlFor="password"
              sx={labelSx}
            >
              Password
            </Typography>
            <TextField
              sx={textFieldSx}
              id="password"
              variant="outlined"
              fullWidth
              required
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter your password"
              InputProps={{
                sx: {
                  "& input::-ms-reveal": {
                    display: "none",
                  },
                  "& input::-ms-clear": {
                    display: "none",
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: colors.secondary }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </IconButton>
                  </InputAdornment>
                ),
                type: showPassword ? "text" : "password",
              }}
            />
          </Box>
          <Button type="submit" sx={buttonSubmitSx} startIcon={<HomeIcon />}>
            Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminLoginScreen;
